import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from 'convex/react';
import { SignOutButton } from '@clerk/clerk-react';
import { api } from '@aeon-karaoke/cloud-convex';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { Button } from './Button';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';

type EnsureState = 'idle' | 'pending' | 'settled' | 'failed' | 'deleted';

/**
 * Renders children only once the signed-in identity has a live users row.
 * When the row is missing (first sign-in racing the Clerk webhook, or a
 * deployment that receives no webhooks), runs the idempotent `users.ensure`
 * bootstrap — retryable on failure, terminal when the account is soft-deleted.
 * The single bootstrap call site for the app. It sits above the router and
 * holds the `users.me` subscription the route guards read.
 */
export function AccountGate({ children }: { children: ReactNode }) {
  const user = useQuery(api.users.me);
  const [state, setState] = useState<EnsureState>('idle');

  const runEnsure = useCallback(async () => {
    setState('pending');
    const result = await toResult(() => convex.mutation(api.users.ensure, {}));
    if (!result.ok) setState('failed');
    else setState(result.value === 'deleted' ? 'deleted' : 'settled');
  }, []);

  useEffect(() => {
    if (user === null && state === 'idle') void runEnsure();
    if (user && state !== 'idle') setState('idle');
  }, [user, state, runEnsure]);

  if (user) return <>{children}</>;
  if (state === 'deleted') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg">
        <ErrorState message="This account is no longer active." />
        <SignOutButton>
          <Button variant="outline">Sign out</Button>
        </SignOutButton>
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg">
        <ErrorState message="We couldn't set up your account." />
        <Button onClick={() => void runEnsure()}>Retry</Button>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <LoadingState label="Setting the stage…" />
    </div>
  );
}
